import type React from 'react';
import {
  useCallback,
  type PropsWithChildren,
  useEffect,
  useState,
} from 'react';
import { useController } from 'react-hook-form';
import type { FieldValues } from 'react-hook-form';

import type { UserRecentCvData } from '@xing-com/crate-jobs-domain-apply-types';
import { useCvDocumentUploadProps } from '@xing-com/crate-jobs-domain-apply-uploads';

import { DocumentsUpload } from './documents-upload';
import type { UploadFieldProps } from './types';

type CvUploadProps<T extends FieldValues> = UploadFieldProps<T> & {
  isRequired?: boolean;
  showAdditionalDocs: boolean;
  recentCvData?: UserRecentCvData;
};

export const CvDocumentUploadField = <T extends FieldValues>({
  control,
  path,
  isRequired = false,
  updateValue,
  showAdditionalDocs,
  recentCvData,
  trackNwtEvent,
}: PropsWithChildren<CvUploadProps<T>>): React.ReactElement => {
  const [showRequiredUploadError, setShowRequiredUploadError] = useState(false);
  const { uploads, ...docUploadProps } = useCvDocumentUploadProps();

  const {
    field: { ref: cvFieldRef, value: cvFieldValue },
    fieldState: { error: cvFieldError },
  } = useController({
    name: path,
    control,
    rules: { required: isRequired },
  });

  const updateUploads = useCallback(
    (updatedUploads: string) => {
      updateValue(updatedUploads, path);
    },
    [updateValue, path]
  );

  useEffect(() => {
    const updatedUploads = uploads
      .map(({ id }) => id)
      .filter(Boolean)
      .join(',');

    if (cvFieldValue !== updatedUploads && !recentCvData?.userRecentCvs?.length)
      updateUploads(updatedUploads);
  }, [
    uploads,
    cvFieldValue,
    recentCvData?.userRecentCvs?.length,
    updateUploads,
  ]);

  useEffect(() => {
    if (cvFieldError?.type === 'required') {
      setShowRequiredUploadError(true);
    } else {
      setShowRequiredUploadError(false);
    }
  }, [cvFieldError]);

  return (
    <DocumentsUpload
      uploads={uploads}
      {...docUploadProps}
      buttonRef={cvFieldRef}
      trackingOrigin="cv"
      maxFiles={1}
      showAdditionalDocs={showAdditionalDocs}
      isRequired={isRequired}
      recentCvData={recentCvData}
      updateUploads={updateUploads}
      showRequiredUploadError={showRequiredUploadError}
      trackNwtEvent={trackNwtEvent}
    />
  );
};
